<template>
  <div class="member-edit">
    <form class="member-edit__form" @submit.prevent="submit">
      <div class="member-edit__content">
        <div class="member-edit__row row">
          <div class="col-12 col-md-6">
            <AppControl
              label="Имя участника"
              class="member-edit__control"
              :error="getError('first_name')"
            >
              <AppInput
                v-model="$v.form.first_name.$model"
                class="member-edit__input"
                :invalid="isInvalid('first_name')"
                placeholder="Введите имя"
                theme="third"
              />
            </AppControl>
          </div>

          <div class="col-12 col-md-6">
            <AppControl
              label="Фамилия участника"
              class="member-edit__control"
              :error="getError('last_name')"
            >
              <AppInput
                v-model="$v.form.last_name.$model"
                class="member-edit__input"
                :invalid="isInvalid('last_name')"
                placeholder="Введите фамилию"
                theme="third"
              />
            </AppControl>
          </div>

          <div class="col-12 col-md-6">
            <AppControl
              label="Отчество участника"
              class="member-edit__control"
              :error="getError('middle_name')"
            >
              <AppInput
                v-model="$v.form.middle_name.$model"
                class="member-edit__input"
                :invalid="isInvalid('middle_name')"
                placeholder="Введите отчество"
                theme="third"
              />
            </AppControl>
          </div>

          <div class="col-12 col-md-6">
            <AppControl
              label="Дата рождения участника"
              class="member-edit__control"
              :error="getError('birthday')"
            >
              <AppDatePicker
                v-model="$v.form.birthday.$model"
                format="YYYY-MM-DD"
                :invalid="isInvalid('birthday')"
                class="member-edit__input"
                placeholder="Выберите даты"
                theme="third"
                :max-date="maxDate"
                @change="clearError('birthday')"
              />
            </AppControl>
          </div>

          <div class="col-12 col-md-6">
            <AppControl
              label="Телефон участника"
              class="member-edit__control"
              :error="getError('mobile_number')"
            >
              <AppInput
                v-mask="'+7##########'"
                v-model="$v.form.mobile_number.$model"
                class="member-edit__input"
                :invalid="isInvalid('mobile_number')"
                placeholder="Введите телефон"
                theme="third"
                @input="clearError('mobile_number')"
              />
            </AppControl>
          </div>

          <div class="col-12 col-md-6">
            <AppControl
              label="E-mail участника"
              class="member-edit__control"
              :error="getError('email')"
            >
              <AppInput
                v-model="$v.form.email.$model"
                class="member-edit__input"
                :invalid="isInvalid('email')"
                placeholder="Введите E-mail"
                theme="third"
                type="email"
                @input="clearError('email')"
              />
            </AppControl>
          </div>

          <div class="col-12">
            <div class="member-edit__bottom">
              <AppCheckboxLabel>
                <AppCheckbox
                  v-model="$v.form.type.$model"
                  :true-value="MemberType.Captain"
                  :false-value="MemberType.Player"
                />
                <span>Капитан</span>
              </AppCheckboxLabel>
              <button
                type="button"
                class="member-edit__own"
                @click="setCurrentUserData"
              >
                Подставить свои данные
              </button>
            </div>
          </div>
        </div>
      </div>

      <ModalMessage>{{ message }}</ModalMessage>

      <AppBtn
        type="submit"
        class="member-edit__submit"
        color="third"
        :loading="loading"
        >{{ isEdit ? "Сохранить" : "Добавить участника" }}</AppBtn
      >
    </form>
  </div>
</template>

<script>
import AppControl from "@/components/ui/control/AppControl.vue";
import { getValidationMessage } from "@utils/validation-messages";
import { required, email } from "vuelidate/lib/validators";
import { requestAlert } from "@/core/common/";
import { asyncModal } from "@/components/ui/modal/";
import cloneDeep from "lodash/cloneDeep";
import pick from "lodash/pick";
import { mapActions } from "vuex";
import { mask } from "vue-the-mask";
import MemberType from "./MemberType";
import { mapGetters } from "vuex";
import { ModalMessage } from "@/components/ui/modal/";

const fiveYears = 1000 * 60 * 60 * 24 * 365 * 5;

const defaultForm = () => ({
  id: null,
  first_name: "",
  last_name: "",
  middle_name: "",
  birthday: "", // В v-date-picker значение value хранится в формате объекта Date()
  mobile_number: "",
  type: MemberType.Player,
  email: ""
});

const MemberEdit = {
  name: "MemberEdit",

  components: {
    AppControl,
    ModalMessage
  },

  directives: {
    mask
  },

  props: {
    member: {
      type: Object,
      default: defaultForm
    }
  },

  data: () => ({
    form: null,
    loading: false,
    message: "",
    formErrors: {},
    maxDate: Date.now() - fiveYears,
    MemberType
  }),

  computed: {
    ...mapGetters("auth", ["user"]),
    isEdit() {
      return !!this.member.id;
    },
    action() {
      return this.isEdit ? this.updateMember : this.addMember;
    }
  },

  watch: {
    member: {
      deep: true,
      immediate: true,
      handler(value) {
        const form = value || {};
        this.form = cloneDeep({ ...defaultForm(), ...form });
      }
    }
  },

  validations: {
    form: {
      first_name: { required },
      last_name: { required },
      middle_name: { required },
      birthday: { required },
      email: { required, email },
      type: { required },
      mobile_number: { required }
    }
  },

  methods: {
    ...mapActions("team", ["updateMember", "addMember"]),

    clearError(field) {
      delete this.formErrors[field];
    },

    getError(field) {
      return (
        getValidationMessage(this.$v.form[field]) ||
        (this.formErrors[field] && this.formErrors[field][0])
      );
    },

    isInvalid(field) {
      return this.$v.form[field].$error || !!this.formErrors[field];
    },

    setCurrentUserData() {
      const data = pick(this.user, [
        "birthday",
        "email",
        "first_name",
        "last_name",
        "middle_name",
        "mobile_number"
      ]);
      Object.assign(this.form, data);
    },

    async submit() {
      if (this.loading) return;
      this.formErrors = {};
      this.$v.$touch();
      this.message = "";
      if (this.$v.$invalid) return;
      this.loading = true;

      try {
        const { success, data, message } = await this.action(this.form);

        if (success) {
          this.$modals.close(this);
        } else {
          this.formErrors = data || {};
          this.message = message || "Не удалось создать участника.";
        }
      } catch {
        requestAlert();
      }

      this.loading = false;
    }
  }
};

export default MemberEdit;

export function openModal(member) {
  return asyncModal({
    name: "member-modal",
    component: MemberEdit,
    props: { member },
    dismissable: false,
    title: member ? "Редактирование участника" : "Добавление участника"
  });
}
</script>

<style lang="scss">
.member-edit {
  width: 665px;
  max-width: 100%;
  margin: 0 auto;
  color: colors(text-2);

  &__content {
    max-width: 100%;
    margin: 0 auto;
  }

  &__control {
    margin-bottom: 20px;
  }

  &__input {
    font-size: 20px;
    text-transform: uppercase;
    font-weight: bold;
  }

  &__submit {
    width: 290px;
    height: 50px;
    text-transform: uppercase;
    margin: 0 auto;
    display: flex;
    font-size: 20px;
    max-width: 100%;
  }

  &__bottom {
    display: flex;
    justify-content: space-between;
  }

  &__own {
    padding: 0 10px;
  }

  @media (max-width: 380px) {
    &__bottom {
      flex-direction: column;
      height: 60px;
    }
    &__own {
      text-align: left;
      padding: 0;
    }
  }
}
</style>
